import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';

import { DatetimeRangePickerComponent } from '@components/shared/datetime-range-picker/datetime-range-picker.component';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';

/** テーブルカラム */
export const Notification_TABLE_COLUMN_LIST: string[] = [
  'releaseAt',
  'title',
  // 'status',
  'updatedAt',
  // 'content',
  'updatedBy',
  'operation',
];

export type Status = '投稿予約' | '投稿済み';

/** 掲示板のViewModel
 * 掲示板メッセージ表示/詳細ポップアップにおいて不要な値はオプショナル(?)とする
 */
export type NotificationViewModel = {
  /** メッセージID */
  messageId: string;
  /** 投稿日時 */
  releaseAt: string;
  /** 投稿ステータス */
  status?: Status;
  /** 最終編集者 */
  updatedBy?: string;
  /** 最終編集日時 */
  updatedAt?: string;
  /** タイトル */
  title: string;
  /** 内容 */
  content: string;
  /** 既読か */
  isRead?: boolean;
};

@Component({
  selector: 'app-notification-list-table',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DatetimeRangePickerComponent,
    DirectivesModule,
  ],
  templateUrl: './notification-list-table.component.html',
  styleUrl: './notification-list-table.component.scss',
})
export class NotificationListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** 新規登録を通知 */
  @Output() created = new EventEmitter<void>();

  /** 更新を通知 */
  @Output() updated = new EventEmitter<NotificationViewModel>();

  /** 削除を通知 */
  @Output() deleted = new EventEmitter<NotificationViewModel>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** 掲示板メッセージ一覧 */
  @Input({ required: true }) public notificationList: NotificationViewModel[] =
    [];

  /** 投稿ステータス一覧 */
  @Input() statusList: string[] = [];

  /** カラム */
  public displayedColumns: string[] = Notification_TABLE_COLUMN_LIST;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<NotificationViewModel>([]);

  /** ステータス検索キーワード */
  public statusKeyword = '';
  /** 最終編集者検索キーワード */
  public updatedByKeyword = '';
  /** タイトル検索キーワード */
  public titleKeyword = '';
  /** 内容検索キーワード */
  public contentKeyword = '';

  constructor() {}

  public ngOnInit(): void {
    this.dataSource.data = this.notificationList;
    this.dataSource.sort = this.sort;

    this.applyFilter();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // フィルター設定
    this.dataSource.filterPredicate = (element: NotificationViewModel) => {
      let status = true;
      if (this.statusKeyword) {
        // statusKeywordが空でない場合のみフィルタリングを行う
        status =
          element
            .status!.trim()
            .toLowerCase()
            .indexOf(this.statusKeyword.toLowerCase()) > -1;
      }

      let updatedBy = true;
      if (this.updatedByKeyword) {
        // updatedByKeywordが空でない場合のみフィルタリングを行う
        updatedBy =
          element
            .updatedBy!.trim()
            .toLowerCase()
            .indexOf(this.updatedByKeyword.toLowerCase()) > -1;
      }

      let title = true;
      if (this.titleKeyword) {
        // titleKeywordが空でない場合のみフィルタリングを行う
        title =
          element.title
            .trim()
            .toLowerCase()
            .indexOf(this.titleKeyword.toLowerCase()) > -1;
      }

      let content = true;
      if (this.contentKeyword) {
        // contentKeywordが空でない場合のみフィルタリングを行う
        content =
          element.content
            .trim()
            .toLowerCase()
            .indexOf(this.contentKeyword.toLowerCase()) > -1;
      }

      return status && updatedBy && title && content;
    };
  }
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['notificationList'] !== undefined) {
      const notificationList: NotificationViewModel[] =
        changes['notificationList'].currentValue;
      this.dataSource.data = notificationList;
    }
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }
  /**
   * ステータスでのフィルタリングをクリアする。
   */
  public clearStatusFilter(): void {
    // フィルタ条件をクリア
    this.statusKeyword = '';
    this.applyFilter();
  }
  /**
   * 最終編集者でのフィルタリングをクリアする。
   */
  public clearUpdatedByFilter(): void {
    // フィルタ条件をクリア
    this.updatedByKeyword = '';
    this.applyFilter();
  }
  /**
   * タイトルでのフィルタリングをクリアする。
   */
  public clearTitleFilter(): void {
    // フィルタ条件をクリア
    this.titleKeyword = '';
    this.applyFilter();
  }
  /**
   * 内容でのフィルタリングをクリアする。
   */
  public clearContentFilter(): void {
    // フィルタ条件をクリア
    this.contentKeyword = '';
    this.applyFilter();
  }

  /**
   * 投稿予定日が現在日時より遅いか比較する(遅い場合はTrueを返す)
   */
  public isPosted(releaseAt: string): boolean {
    const releaseAtDt = new Date(releaseAt.trim().toLowerCase());
    return releaseAtDt < new Date();
  }
}
